import { useLocation, useNavigate } from "react-router";
import { useState, useEffect, useMemo } from "react";
import { Tree } from "antd";
import { ErrorBoundary } from "react-error-boundary";
import { useDispatch } from "react-redux";

import {
  FOLDER_PARAMETER,
  PAGE_PARAMETER,
} from "helpers/constants/miscellaneous";

import { FilterOption, TreeData } from "types";

import NoMatchFoundIcon from "assets/icons/NoMatchFoundIcon";
import SearchIcon from "assets/icons/SearchIcon";
import XIcon from "assets/icons/XIcon";
import styles from "./style.module.scss";
import "./tree.css";

import { fetchTreeFolder } from "redux/SingleDataset/actions";
import { trackFilterSelection } from "helpers/utility/amplitude";
import { debounceCreator } from "helpers/utility/utilities";
import FilterButton from "views/atomComponents/FilterButton";
import useQueryParam from "hooks/useQueryParam";

const { DirectoryTree } = Tree;

const NoMatchComponent = () => {
  return (
    <div className={styles.noSearchDataBox}>
      <NoMatchFoundIcon />
      <span className={styles.noSearchDataText}>No matches found</span>
      <span className={styles.noSearchDataText}>Please try another term</span>
    </div>
  );
};

interface FolderFilterContentProps {
  treeData: TreeData[] | null;
  selectedFolder: string;
  entityTypeFilter: FilterOption[];
  onSelectFolder: (path: string) => void;
}

function FolderFilterContent({
  treeData,
  selectedFolder,
  onSelectFolder,
  entityTypeFilter,
}: FolderFilterContentProps) {
  const [checkedKeys, setCheckedKeys] = useState(
    !!selectedFolder ? [selectedFolder] : []
  );
  const [expandedKeys, setExpandedKeys] = useState(checkedKeys);
  const [searchValue, setSearchValue] = useState("");

  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const filterTreeData = (data: any) => {
    return data
      .filter((item: any) => {
        const itemText = item.title.toLowerCase();
        const searchText = searchValue.toLowerCase();
        return (
          itemText.includes(searchText) ||
          (item.children && filterTreeData(item.children).length > 0)
        );
      })
      .map((filteredItem: any) => {
        if (filteredItem.children) {
          return {
            ...filteredItem,
            children: filterTreeData(filteredItem.children),
          };
        }
        return filteredItem;
      });
  };

  const getExpandedKeys = (data: any, searchText: string): string[] => {
    const keys: string[] = [];
    data.forEach((item: any) => {
      const itemText = item.title.toLowerCase();
      const lowerSearchText = searchText.toLowerCase();
      if (itemText.includes(lowerSearchText)) {
        keys.push(item.key);
        if (item.children) {
          keys.push(...getExpandedKeys(item.children, searchText));
        }
      } else if (item.children) {
        const childKeys = getExpandedKeys(item.children, searchText);
        if (childKeys.length > 0) {
          keys.push(item.key);
          keys.push(...childKeys);
        }
      }
    });

    return keys;
  };

  const onCheck = debounceCreator((e: any) => {
    let selection = null;
    if (e.checked.length > 0) {
      selection = e.checked.slice(-1)[0];
      setCheckedKeys(e.checked.slice(-1));
    } else {
      setCheckedKeys(e.checked);
    }

    if (selection) {
      trackFilterSelection("Files", "Dataset", entityTypeFilter, "Added");
    } else {
      trackFilterSelection("Files", "Dataset", entityTypeFilter, "Removed");
    }

    onSelectFolder(selection);
  }, 500);

  const onExpand = (expandedKeysValue: any, e: any) => {
    e.nativeEvent.preventDefault();
    setExpandedKeys(expandedKeysValue);
  };

  const clearSearch = (e: any) => {
    e.stopPropagation();
    setSearchValue("");
  };

  const clearSelection = (e: any) => {
    e.stopPropagation();
    setSearchValue("");
    setCheckedKeys([]);
    setExpandedKeys([]);

    searchParams.delete(FOLDER_PARAMETER);
    searchParams.set(PAGE_PARAMETER, "1");
    navigate(location.pathname + `?${searchParams.toString()}`);
  };

  const onSearch = (e: any) => {
    const { value } = e.target;
    if (value) {
      const keys = getExpandedKeys(treeData, value);

      setExpandedKeys(keys);
      setSearchValue(value);
    } else {
      setExpandedKeys(checkedKeys);
      setSearchValue("");
    }
  };

  const filteredTreeData = filterTreeData(treeData);
  return (
    <div className={styles.dropdown}>
      <div className={styles.searchBarContainer}>
        {!searchValue && (
          <div className={styles.searchIcon}>
            <SearchIcon />
          </div>
        )}
        <input
          type="search"
          autoComplete="off"
          onChange={onSearch}
          value={searchValue}
          className={styles.searchBarInput}
        />
        {searchValue && (
          <div className={styles.clearSearchIcon} onClick={clearSearch}>
            <XIcon />
          </div>
        )}
      </div>
      {checkedKeys.length > 0 && (
        <div className={styles.clearSelectionButton} onClick={clearSelection}>
          Clear Selection
        </div>
      )}
      <div className={styles.horizontalRule}></div>
      {!!treeData && (
        <ErrorBoundary fallback={<div>Something went wrong</div>}>
          <DirectoryTree
            checkable
            checkStrictly
            checkedKeys={checkedKeys}
            expandedKeys={expandedKeys}
            expandAction={"doubleClick"}
            selectable={false}
            onCheck={onCheck}
            onExpand={(arr: any, e: any) => onExpand(arr, e)}
            treeData={filteredTreeData}
          />
          {searchValue && filteredTreeData.length < 1 && <NoMatchComponent />}
        </ErrorBoundary>
      )}
    </div>
  );
}

interface FolderFilterProps {
  datasetId: string;
  treeData: TreeData[] | null;
  onSelect: Function;
  entityTypeFilter: any;
  isFileFilterLoading: boolean;
}

function FolderFilter({
  datasetId,
  treeData,
  onSelect,
  entityTypeFilter,
  isFileFilterLoading,
}: FolderFilterProps) {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchTreeFolder(datasetId) as any);
    //eslint-disable-next-line
  }, [datasetId]);
  const onSelectFolder = (path: string) => {
    onSelect(path);
  };
  const selectedFolder = useQueryParam(FOLDER_PARAMETER);
  const title = selectedFolder ? "Folder Selected" : "File/Folder";

  if (isFileFilterLoading) {
    return <div className={styles.folderLoader}></div>;
  } else {
    return (
      <FilterButton
        label={title}
        variant={!!selectedFolder ? "selected" : "default"}
        tooltip={`Apply this filter to display only the data entries from a specific file or folder.`}
      >
        <FolderFilterContent
          treeData={treeData}
          onSelectFolder={onSelectFolder}
          selectedFolder={selectedFolder || ""}
          entityTypeFilter={entityTypeFilter}
        />
      </FilterButton>
    );
  }
}

export default FolderFilter;
